import i18n from "i18next";
import { initReactI18next } from "react-i18next";

// The few strings that have no counterpart in the Rails locales. They cannot
// live in locales/*.json: that directory is regenerated by `npm run locales`
// and anything added there by hand is lost on the next import. All three
// languages sit in one file, so a forgotten translation is visible where the
// string is written rather than months later as a German word on an Italian
// screen. scripts/check-i18n.mjs verifies both sources against every t() call.
import appStrings from "./appStrings.json";
import de from "./locales/de.json";
import en from "./locales/en.json";
import it from "./locales/it.json";

export const SUPPORTED_LOCALES = ["de", "en", "it"] as const;
export type SupportedLocale = (typeof SUPPORTED_LOCALES)[number];

/** Matches the site default in the Rails app. */
export const FALLBACK_LOCALE: SupportedLocale = "it";

const STORAGE_KEY = "myspeechplan.locale";

export function isSupportedLocale(value: string | null | undefined): value is SupportedLocale {
  return !!value && (SUPPORTED_LOCALES as readonly string[]).includes(value);
}

/**
 * The server resolves its own language per request (user preference, then
 * Accept-Language, then site default) and answers notifications in it. This
 * only decides which bundle the *client* renders its own labels from, and it is
 * sent along as Accept-Language so both sides agree.
 */
function initialLocale(): SupportedLocale {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY);
    if (isSupportedLocale(stored)) return stored;
  } catch {
    /* ignore */
  }

  const fromBrowser = navigator.languages
    ?.map((tag) => tag.split("-")[0].toLowerCase())
    .find(isSupportedLocale);

  return fromBrowser ?? FALLBACK_LOCALE;
}

export function setLocale(locale: SupportedLocale): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, locale);
  } catch {
    /* ignore */
  }
  void i18n.changeLanguage(locale);
}

export function currentLocale(): SupportedLocale {
  return isSupportedLocale(i18n.language) ? i18n.language : FALLBACK_LOCALE;
}

void i18n.use(initReactI18next).init({
  // The imported Rails keys are the base; the handful of app-only strings is
  // added beside them under "app.".
  resources: {
    de: { translation: { ...de, app: appStrings.de } },
    en: { translation: { ...en, app: appStrings.en } },
    it: { translation: { ...it, app: appStrings.it } },
  },
  lng: initialLocale(),
  fallbackLng: FALLBACK_LOCALE,
  interpolation: { escapeValue: false },
  // The Rails keys are dotted paths ("user.password_updated"), which is exactly
  // i18next's default nesting separator — so keys can be copied over verbatim.
  keySeparator: ".",
  returnNull: false,
});

export default i18n;
